import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SettingsState {
  // Appearance settings
  compactMode: boolean;
  highContrast: boolean;
  
  // Notification preferences
  assessmentDueReminders: boolean;
  reportGenerationComplete: boolean;
  emailDigest: boolean;
  systemUpdates: boolean;
  
  // Actions
  setCompactMode: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  setAssessmentDueReminders: (enabled: boolean) => void;
  setReportGenerationComplete: (enabled: boolean) => void;
  setEmailDigest: (enabled: boolean) => void;
  setSystemUpdates: (enabled: boolean) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      // Default values
      compactMode: false,
      highContrast: false,
      assessmentDueReminders: true,
      reportGenerationComplete: true,
      emailDigest: false,
      systemUpdates: true,
      
      // Actions
      setCompactMode: (enabled) => {
        set({ compactMode: enabled });
        // Apply/remove compact mode class to document
        if (enabled) {
          document.documentElement.classList.add('compact-mode');
        } else {
          document.documentElement.classList.remove('compact-mode');
        }
      },
      setHighContrast: (enabled) => {
        set({ highContrast: enabled });
        // Apply/remove high contrast class to document
        if (enabled) {
          document.documentElement.classList.add('high-contrast');
        } else {
          document.documentElement.classList.remove('high-contrast');
        }
      },
      setAssessmentDueReminders: (enabled) => set({ assessmentDueReminders: enabled }),
      setReportGenerationComplete: (enabled) => set({ reportGenerationComplete: enabled }),
      setEmailDigest: (enabled) => set({ emailDigest: enabled }),
      setSystemUpdates: (enabled) => set({ systemUpdates: enabled }),
    }),
    {
      name: 'cdrs-settings',
      onRehydrateStorage: () => (state) => {
        // Apply settings on rehydration (page load)
        if (state) {
          if (state.compactMode) {
            document.documentElement.classList.add('compact-mode');
          }
          if (state.highContrast) {
            document.documentElement.classList.add('high-contrast');
          }
        }
      },
    }
  )
);

// Notification store for managing notifications
interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: Date;
  link?: string;
}

interface NotificationState {
  notifications: Notification[];
  isOpen: boolean;
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  unreadCount: () => number;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [
    // Default demo notifications
    {
      id: '1',
      title: 'Assessment Due',
      message: 'Emma Johnson has an assessment due in 3 days',
      type: 'warning',
      read: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 30), // 30 min ago
      link: '/app/children',
    },
    {
      id: '2',
      title: 'Report Ready',
      message: 'Progress report for Michael Chen is ready for download',
      type: 'success',
      read: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
      link: '/app/reports',
    },
  ],
  isOpen: false,
  
  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      read: false,
      createdAt: new Date(),
    };
    set((state) => ({
      notifications: [newNotification, ...state.notifications],
    }));
  },
  
  markAsRead: (id) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    }));
  },
  
  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
    }));
  },
  
  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },
  
  clearAll: () => {
    set({ notifications: [] });
  },
  
  setOpen: (open) => set({ isOpen: open }),
  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
  
  unreadCount: () => {
    return get().notifications.filter((n) => !n.read).length;
  },
}));
